import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';

export default function DataDiagnostic() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState({
    authenticated: false,
    userId: null,
    customers: { total: 0, withUserId: 0, withoutUserId: 0 },
    estimates: { total: 0, withUserId: 0, withoutUserId: 0 },
    jobs: { total: 0, withUserId: 0, withoutUserId: 0 },
    inventory: { total: 0, withUserId: 0, withoutUserId: 0 },
    errors: []
  });

  useEffect(() => {
    const runDiagnostics = async () => {
      setLoading(true);
      const results = {
        authenticated: !!currentUser,
        userId: currentUser?.uid || null,
        customers: { total: 0, withUserId: 0, withoutUserId: 0 },
        estimates: { total: 0, withUserId: 0, withoutUserId: 0 },
        jobs: { total: 0, withUserId: 0, withoutUserId: 0 },
        inventory: { total: 0, withUserId: 0, withoutUserId: 0 },
        errors: []
      };

      if (!currentUser) {
        setDiagnostics(results);
        setLoading(false);
        return;
      }

      // Check each collection
      const collections = ['customers', 'estimates', 'jobs', 'inventory'];
      
      for (const collectionName of collections) {
        try {
          // Try to get all documents (this might fail due to security rules)
          const allQuery = collection(db, collectionName);
          const allSnapshot = await getDocs(allQuery);
          results[collectionName].total = allSnapshot.size;
          
          // Count documents with and without userId
          allSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data.userId === currentUser.uid) {
              results[collectionName].withUserId++;
            } else {
              results[collectionName].withoutUserId++;
            }
          });
        } catch (error) {
          results.errors.push(`${collectionName}: ${error.message}`);
          
          // Try with userId filter
          try {
            const filteredQuery = query(
              collection(db, collectionName),
              where('userId', '==', currentUser.uid)
            );
            const filteredSnapshot = await getDocs(filteredQuery);
            results[collectionName].withUserId = filteredSnapshot.size;
          } catch (filterError) {
            results.errors.push(`${collectionName} (filtered): ${filterError.message}`);
          }
        }
      }

      setDiagnostics(results);
      setLoading(false);
    };

    runDiagnostics();
  }, [currentUser]);

  const migrateData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    const collections = ['customers', 'estimates', 'jobs', 'inventory'];
    let migrated = 0;
    let errors = [];

    for (const collectionName of collections) {
      try {
        const allQuery = collection(db, collectionName);
        const snapshot = await getDocs(allQuery);
        
        for (const docSnapshot of snapshot.docs) {
          const data = docSnapshot.data();
          if (!data.userId) {
            try {
              const { updateDoc, doc: docRef } = await import('firebase/firestore');
              await updateDoc(docRef(db, collectionName, docSnapshot.id), {
                userId: currentUser.uid
              });
              migrated++;
            } catch (updateError) {
              errors.push(`${collectionName}/${docSnapshot.id}: ${updateError.message}`);
            }
          }
        }
      } catch (error) {
        errors.push(`${collectionName}: ${error.message}`);
      }
    }

    alert(`Migration complete!\nMigrated: ${migrated} documents\nErrors: ${errors.length}`);
    if (errors.length > 0) {
      console.error('Migration errors:', errors);
    }
    setLoading(false);
    // Reload diagnostics
    window.location.reload();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Diagnostic
      </Typography>

      <Alert severity={diagnostics.authenticated ? 'success' : 'error'} sx={{ mb: 3 }}>
        {diagnostics.authenticated 
          ? `Authenticated as: ${diagnostics.userId}`
          : 'Not authenticated. Please log in.'}
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Collection Status
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Collection</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>With UserId</strong></TableCell>
                <TableCell align="right"><strong>Without UserId</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Customers</TableCell>
                <TableCell align="right">{diagnostics.customers.total}</TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.customers.withUserId} color="success" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.customers.withoutUserId} color="warning" size="small" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Estimates</TableCell>
                <TableCell align="right">{diagnostics.estimates.total}</TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.estimates.withUserId} color="success" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.estimates.withoutUserId} color="warning" size="small" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Jobs</TableCell>
                <TableCell align="right">{diagnostics.jobs.total}</TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.jobs.withUserId} color="success" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.jobs.withoutUserId} color="warning" size="small" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Inventory</TableCell>
                <TableCell align="right">{diagnostics.inventory.total}</TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.inventory.withUserId} color="success" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={diagnostics.inventory.withoutUserId} color="warning" size="small" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {diagnostics.errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Errors:</Typography>
          <ul>
            {diagnostics.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {diagnostics.authenticated && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Data Recovery
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you have documents without a userId field, you can migrate them to add your userId.
            This will make them visible in your account.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={migrateData}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            Migrate Data (Add userId to documents)
          </Button>
        </Paper>
      )}
    </Box>
  );
}

